<template>
  <div class="c-rate-history">
    <v-dialog
      v-model="show"
      width="60vw"
      max-width="calc(100vw - 30px)"
    >
      <v-card v-if="employee">
        <v-card-title>
          Rate history
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            :key="updateTable"
            :headers="headers"
            :items="employee.Employee_Rate"
            :footer-props="{
              'items-per-page-options': [10, 30, 50, -1]
            }"
            :items-per-page="50"
          >
            <template v-slot:[`item.Rate_Date`]="{ item }">
              <DatePicker
                v-if="showEditFields(item) && canEdit"
                v-model="currentItem.Rate_Date"
                :min="minDate"
                :max="maxDate"
              />
              <span v-else>
                {{ item.Rate_Date | dateFilter }}
              </span>
            </template>
            <template v-slot:[`item.Rate`]="{ item }">
              <v-text-field
                v-if="showEditFields(item) && canEdit"
                v-model="currentItem.Rate"
                type="number"
              ></v-text-field>
              <span v-else>{{ item.Rate }}</span>
            </template>
            <template v-slot:[`item.Salary`]="{ item }">
              <v-text-field
                v-if="showEditFields(item) && canEdit"
                v-model="currentItem.Salary"
                type="number"
              ></v-text-field>
              <span v-else>{{ item.Salary }}</span>
            </template>
            <template v-slot:[`item.Salary_Type`]="{ item }">
              <v-select
                v-if="showEditFields(item) && canEdit"
                v-model="currentItem.Salary_Type"
                :items="salaryTypes"
              ></v-select>
              <span v-else>{{ item.Salary_Type }}</span>
            </template>
            <template v-slot:[`item.Actions`]="{ item, index }">
              <v-btn icon small
                :color="showEditFields(item) ? 'success' : 'primary'"
                :disabled="addNewMode || (editMode && currentItemToggle !== index)"
                @click.prevent="editModeToggle(item, index)"
              >
                <v-icon>
                  {{ showEditFields(item) ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                icon small
                v-if="showEditFields(item) && canEdit"
                color="primary"
                :disabled="addNewMode"
                @click.prevent="editMode = false; currentItemToggle = -1"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
              <v-btn icon small
                v-if="!showEditFields(item)"
                color="red darken-2"
                :disabled="addNewMode || (editMode && currentItemToggle !== index)"
                @click.prevent="showRemoveDialog(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
            </template>
            <template v-slot:[`body.append`] v-if="canEdit">
              <td
                class="px-2 text-no-wrap"
                :class="{
                  'v-data-table__divider': addNewMode
                }"
              >
              <DatePicker
                v-if="addNewMode"
                v-model="currentItem.Rate_Date"
                :min="minDate"
                :max="maxDate"
              />
              </td>
              <td
                class="px-2 text-no-wrap"
                :class="{
                  'v-data-table__divider': addNewMode
                }"
              >
                <v-text-field
                  v-if="addNewMode"
                  type="number"
                  v-model="currentItem.Rate"
                ></v-text-field>
              </td>
              <td
                class="px-2 text-no-wrap"
                :class="{
                  'v-data-table__divider': addNewMode
                }"
              >
                <v-text-field
                  v-if="addNewMode"
                  type="number"
                  v-model="currentItem.Salary"
                ></v-text-field>
              </td>
              <td
                class="px-2 text-no-wrap"
                :class="{
                  'v-data-table__divider': addNewMode
                }"
              >
                <v-select
                  v-if="addNewMode"
                  :items="salaryTypes"
                  v-model="currentItem.Salary_Type"
                ></v-select>
              </td>
              <td class="text-center text-no-wrap">
                <v-btn
                  icon
                  color="success"
                  small
                  :disabled="editMode"
                  @click.prevent="addNewModeToggle"
                >
                  <v-icon>
                    {{ addNewMode ? 'mdi-check-circle' : 'mdi-plus-circle' }}
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="addNewMode"
                  icon
                  color="primary"
                  small
                  @click.prevent="addNewMode = false"
                >
                  <v-icon>mdi-close-circle</v-icon>
                </v-btn>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Remove dialog -->
    <ConfirmOrDeniedDialog
      v-if="removeDialog"
      v-model="removeDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="removeRate"
      @onCancelClick="removeDialog = false"
    />
    <!-- /Remove dialog -->
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'RateHistory',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    employee: {
      required: false,
      default: null
    },
    canEdit: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      salaryTypes: ['RATE', 'BASE', 'OFFICE'],
      currentItem: {
        Employee_Rate_ID: null,
        Employee_ID: null,
        Rate: 0,
        Rate_Date: formatLocalDate(new Date()),
        Salary: 0,
        Salary_Type: 'BASE'
      },
      updateTable: 0,
      addNewMode: false,
      editMode: false,
      currentItemToggle: -1,
      removeDialog: false
    }
  },
  computed: {
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    showEditFields () {
      return item => this.editMode && !this.addNewMode && item.Employee_Rate_ID === this.currentItem.Employee_Rate_ID
    },
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    headers () {
      const res = [
        {
          text: 'Rate date',
          value: 'Rate_Date',
          divider: true,
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        },
        {
          text: 'Rate',
          value: 'Rate',
          divider: true,
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        },
        {
          text: 'Salary',
          value: 'Salary',
          divider: true,
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        },
        {
          text: 'Salary type',
          value: 'Salary_Type',
          divider: true,
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        }
      ]
      if (this.canEdit) {
        res.push({
          text: '#',
          align: 'center',
          value: 'Actions',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        })
      }
      return res
    },
    minDate () {
      let ind = -1
      let res = null
      const from = this.addNewMode ? 0 : 1
      for (let i = 0; i < this.employee.Employee_Rate.length; i++) {
        ind = i > 0 ? i - from : -from
        if (this.currentItem.Employee_Rate_ID === this.employee.Employee_Rate[i].Employee_Rate_ID) {
          break
        }
      }
      if (ind > -1) {
        const date = new Date(this.employee.Employee_Rate[ind].Rate_Date)
        res = formatLocalDate(new Date(date.setDate(date.getDate() + 1)))
      }
      return res
    },
    maxDate () {
      let ind = -1
      let res = null
      for (let i = 0; i < this.employee.Employee_Rate.length; i++) {
        if (this.currentItem.Employee_Rate_ID === this.employee.Employee_Rate[i].Employee_Rate_ID) {
          ind = i < this.employee.Employee_Rate.length - 1 ? i + 1 : -1
          break
        }
      }
      if (ind > -1) {
        const date = new Date(this.employee.Employee_Rate[ind].Rate_Date)
        res = formatLocalDate(new Date(date.setDate(date.getDate() - 1)))
      }
      return res
    }
  },
  methods: {
    editModeToggle (item, index) {
      if (this.editMode) {
        this.axios.put(`/api/employee_rate/${this.currentItem.Employee_Rate_ID}`, this.currentItem, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.$store.dispatch('updateEmployeeRate', { rate: res.data[0] })
          this.$store.dispatch('setSuccess', { successMsg: 'Rate successfully updated' })
          this.updateTable++
          this.currentItemToggle = -1
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        this.currentItem = { ...item }
        this.currentItem.Rate_Date = formatLocalDate(this.currentItem.Rate_Date)
      }
      this.editMode = !this.editMode
      this.currentItemToggle = index
    },
    addNewModeToggle () {
      if (this.addNewMode) {
        this.axios.post('/api/employee_rate', this.currentItem, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.$store.dispatch('addEmployeeRate', { rate: res.data[0] })
          this.$store.dispatch('setSuccess', { successMsg: 'Rate successfully added' })
          this.updateTable++
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        this.currentItem.Employee_ID = this.employee.Employee_ID
        this.currentItem.Rate = 0
        this.currentItem.Rate_Date = formatLocalDate(new Date())
        this.currentItem.Salary = 0
        this.currentItem.Salary_Type = 'BASE'
      }
      this.addNewMode = !this.addNewMode
    },
    showRemoveDialog (item) {
      this.currentItem = { ...item }
      this.removeDialog = true
    },
    removeRate () {
      this.removeDialog = false
      this.axios.delete(`/api/employee_rate/${this.currentItem.Employee_Rate_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('removeEmployeeRate', { rate: this.currentItem })
        this.$store.dispatch('setSuccess', { successMsg: 'Rate successfully removed' })
        this.updateTable++
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  },
  filters: {
    dateFilter (val) {
      if (val) return formatLocalDate(val)
      return null
    }
  }
}
</script>
